<template>
  <div class="min-h-screen bg-gray-950 p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative max-w-7xl mx-auto space-y-6">
      <!-- Trial Activation Card (показываем только если можно активировать) -->
      <div
        v-if="canActivateTrial"
        class="card bg-gradient-to-r from-primary-500/10 via-accent-red/10 to-accent-yellow/10 border-primary-500/30"
      >
        <div class="px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div class="flex items-center gap-4">
            <div
              class="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 via-accent-red to-accent-yellow flex items-center justify-center shadow-lg shadow-primary-500/25"
            >
              <svg
                class="w-7 h-7 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-white font-display">
                Попробуйте Pro бесплатно!
              </h3>
              <p class="text-gray-400">
                Активируйте <span class="text-primary-500 font-semibold">14-дневный</span> пробный
                период на любом платном тарифе
              </p>
            </div>
          </div>
          <button
            @click="$router.push('/activate-trial')"
            class="btn-primary whitespace-nowrap flex items-center gap-2 px-6 py-3"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            Активировать Trial
          </button>
        </div>
      </div>

      <!-- Trial Status Card (если на trial) -->
      <div
        v-if="trialStatus && trialStatus.is_on_trial"
        class="card bg-gradient-to-r from-green-500/10 to-green-600/5 border-green-500/30"
      >
        <div class="px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center"
            >
              <svg
                class="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-white font-display">
                Пробный период активен
              </h3>
              <p class="text-gray-400">
                Тариф <span class="text-green-500 font-semibold">{{ trialStatus.current_plan?.name }}</span>
                · Осталось <span class="text-white font-semibold">{{ trialStatus.trial_days_remaining }}</span> дней
              </p>
            </div>
          </div>
          <button
            @click="$router.push('/profile')"
            class="btn-secondary whitespace-nowrap"
          >
            Оформить подписку
          </button>
        </div>
      </div>

      <!-- Welcome Card -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h3 class="text-lg font-semibold text-white font-display">
            Добро пожаловать в pushx.pro
          </h3>
        </div>
        <div class="px-6 py-8 text-center">
          <h2 class="text-3xl font-bold text-gradient mb-4 font-display">
            Панель управления
          </h2>
          <p class="text-gray-400 text-lg">
            Управляйте вашими приложениями PushX
          </p>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Applications Card -->
        <div
          class="card card-hover cursor-pointer group"
          @click="$router.push('/applications')"
        >
          <div class="p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center group-hover:bg-primary-500/30 transition-colors">
                  <svg
                    class="w-6 h-6 text-primary-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-2xl font-bold text-white font-display">
                  {{ applicationsCount }}
                </h3>
                <p class="text-sm text-gray-400">Приложений</p>
              </div>
            </div>
          </div>
        </div>

        <!-- User Card -->
        <div
          class="card card-hover cursor-pointer group"
          @click="$router.push('/profile')"
        >
          <div class="p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="w-12 h-12 bg-accent-red/20 rounded-xl flex items-center justify-center group-hover:bg-accent-red/30 transition-colors">
                  <svg
                    class="w-6 h-6 text-accent-red"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-bold text-white truncate font-display">
                  {{ user?.name || 'Пользователь' }}
                </h3>
                <p class="text-sm text-gray-400">Профиль</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Analytics Card -->
        <div
          class="card card-hover cursor-pointer group"
          @click="$router.push('/analytics')"
        >
          <div class="p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-accent-yellow/20 rounded-xl flex items-center justify-center group-hover:bg-accent-yellow/30 transition-colors"
                >
                  <svg
                    class="w-6 h-6 text-accent-yellow"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-bold text-white font-display">Аналитика</h3>
                <p class="text-sm text-gray-400">Статистика</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards Row 2 -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- SDK Documentation Card -->
        <div
          class="card card-hover cursor-pointer group"
          @click="$router.push('/sdk-documentation')"
        >
          <div class="p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center group-hover:bg-green-500/30 transition-colors"
                >
                  <svg
                    class="w-6 h-6 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-bold text-white font-display">Документация</h3>
                <p class="text-sm text-gray-400">SDK</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Debug Console Card -->
        <div
          class="card card-hover cursor-pointer group"
          @click="$router.push('/debug-console')"
        >
          <div class="p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center group-hover:bg-purple-500/30 transition-colors"
                >
                  <svg
                    class="w-6 h-6 text-purple-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-bold text-white font-display">Debug Console</h3>
                <p class="text-sm text-gray-400">Тестирование</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions Card -->
      <div class="card">
        <div class="px-6 py-4 border-b border-gray-800">
          <h3 class="text-lg font-semibold text-white font-display">Быстрые действия</h3>
        </div>
        <div class="px-6 py-6">
          <div class="flex flex-wrap gap-4">
            <button
              @click="$router.push('/applications')"
              class="btn-primary inline-flex items-center px-5 py-2.5 text-sm font-medium"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                ></path>
              </svg>
              Создать приложение
            </button>
            <button
              @click="$router.push('/sdk-documentation')"
              class="btn-secondary inline-flex items-center px-5 py-2.5 text-sm font-medium"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                ></path>
              </svg>
              Документация SDK
            </button>
            <button
              @click="$router.push('/debug-console')"
              class="btn-secondary inline-flex items-center px-5 py-2.5 text-sm font-medium"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              Debug Console
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/auth';
import { useApplicationsStore } from '../stores/applications';
import api from '../services/api';

export default {
  name: 'Dashboard',
  setup() {
    const authStore = useAuthStore();
    const applicationsStore = useApplicationsStore();

    return {
      authStore,
      applicationsStore,
    };
  },
  data() {
    return {
      trialStatus: null,
    };
  },
  computed: {
    user() {
      return this.authStore.currentUser;
    },
    applicationsCount() {
      return this.applicationsStore.applicationsList.length;
    },
    canActivateTrial() {
      // Показываем если можно активировать trial и сейчас не на trial
      return this.trialStatus?.can_activate && !this.trialStatus?.is_on_trial;
    },
  },
  mounted() {
    this.loadData();
    this.loadTrialStatus();
  },
  methods: {
    loadData() {
      this.applicationsStore.fetchApplications();
    },
    async loadTrialStatus() {
      try {
        const response = await api.get('/trial/status');
        if (response.data.success) {
          this.trialStatus = response.data.data;
        }
      } catch (error) {
        console.error('Failed to load trial status:', error);
      }
    },
  },
};
</script>
